import React, { FC, useMemo, useState } from 'react';
import { Checkbox } from '@shared/ui/kit';
import { EventParticipantCard } from '@features/event-participant-card';
import stylex from '@stylexjs/stylex';
import { styles } from './styles';
import { ChevronDownIcon } from '@shared/assets/icons';
import { EmployeeDepartment } from '@entities/event/lib/hooks/useFetchSegmentTree';
import { Ticket } from '@pages/event/api/useFetchTicketSegment';

interface SegmentStructureTreeProps {
  department: EmployeeDepartment;
  toggleUser?: (ticket: Ticket) => void;
  checkedUsers?: { [ticketId: number]: number };
  setCheckedUsers?: React.Dispatch<React.SetStateAction<{ [ticketId: number]: number }>>;
  search: string;
  onDelete?: (ticket: Ticket) => void;
  onGetQr?: (ticketId: number) => void;
  eventId?: number;
  refetchSegments?: () => void;
  refetchCategories?: () => void;
  eventDateEnd?: string;
  eventDateStart?: string;
}

const SegmentStructureTreeComponent: FC<SegmentStructureTreeProps> = ({
  department,
  toggleUser,
  checkedUsers = {},
  setCheckedUsers,
  search,
  onDelete,
  onGetQr,
  eventId,
  refetchSegments,
  refetchCategories,
  eventDateEnd,
  eventDateStart,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const hasSearch = !!search?.trim();
  const isEffectivelyOpen = hasSearch || isOpen;
  const arrowStyle = isEffectivelyOpen ? styles.chevronOpen : styles.chevronClosed;

  // Собираем все активные тикеты вмсете с вложенными
  const allActiveTickets = useMemo(() => {
    const collect = (dep: EmployeeDepartment): Ticket[] => {
      const ownTickets = dep.tickets ?? [];
      const childTickets = dep.childDepartment?.flatMap(collect) ?? [];
      return [...ownTickets, ...childTickets];
    };
    return collect(department).filter((ticket) => ticket.participant?.active);
  }, [department]);

  // Все ли активные тикеты выбраны
  const allSelected = useMemo(() => {
    return allActiveTickets.length > 0 && allActiveTickets.every((ticket) => checkedUsers[ticket.id]);
  }, [allActiveTickets, checkedUsers]);

  // Обработка чекбокса всего департамента
  const handleDepartmentCheckboxChange = () => {
    const shouldCheck = !allSelected;

    if (setCheckedUsers) {
      setCheckedUsers((prev) => {
        const updated = { ...prev };
        for (const ticket of allActiveTickets) {
          if (shouldCheck) updated[ticket.id] = ticket.participant.id;
          else delete updated[ticket.id];
        }
        return updated;
      });
    }
  };

  const filteredTickets = useMemo(() => {
    const searchLower = search?.toLowerCase().trim();
    if (!searchLower) return department.tickets;

    return department.tickets?.filter((ticket) => {
      const firstName = ticket.participant.firstName?.toLowerCase().trim() || '';
      const lastName = ticket.participant.lastName?.toLowerCase().trim() || '';
      const fullName = `${firstName} ${lastName}`;
      const reversedName = `${lastName} ${firstName}`;

      return (
        fullName.includes(searchLower) ||
        reversedName.includes(searchLower) ||
        firstName.includes(searchLower) ||
        lastName.includes(searchLower)
      );
    });
  }, [search, department.tickets]);

  const noneSelected = useMemo(() => {
    return allActiveTickets.every((ticket) => !checkedUsers[ticket.id]);
  }, [allActiveTickets, checkedUsers]);

  const isIndeterminate = !allSelected && !noneSelected;

  return (
    <>
      <div onClick={() => setIsOpen(!isOpen)} {...stylex.props(styles.departmentHeader)}>
        <div {...stylex.props(styles.departmentHeaderW)}>
          <span {...stylex.props(styles.departmentArrow, arrowStyle)}>
            <ChevronDownIcon />
          </span>
          <span {...stylex.props(styles.checkbox)} onClick={(e) => e.stopPropagation()}>
            <Checkbox
              isChecked={allSelected}
              isIndeterminate={isIndeterminate}
              onChange={handleDepartmentCheckboxChange}
            />
          </span>
          <span {...stylex.props(styles.departmentTitle)}>{department.name}</span>
        </div>
      </div>

      {isEffectivelyOpen && (
        <div {...stylex.props(styles.departmentNode)}>
          <div {...stylex.props(styles.departmentChildren)}>
            {filteredTickets?.map((ticket) => {
              const isDisabled = !ticket.participant?.active;

              return (
                <div key={ticket.id} {...stylex.props(styles.row)}>
                  <EventParticipantCard
                    ticket={ticket}
                    position={ticket.participant.position ?? undefined}
                    name={`${ticket.participant.firstName} ${ticket.participant.lastName}`}
                    checked={!!checkedUsers[ticket.id]}
                    disabled={isDisabled}
                    onToggle={() => toggleUser?.(ticket)}
                    onDelete={() => onDelete?.(ticket)}
                    getTicketQR={() => onGetQr?.(ticket.id)}
                    eventId={eventId}
                    refetchSegments={refetchSegments}
                    refetchCategories={refetchCategories}
                    eventDateEnd={eventDateEnd}
                    eventDateStart={eventDateStart}
                  />
                </div>
              );
            })}

            {department?.childDepartment?.map((child) => (
              <SegmentStructureTree
                key={child.id}
                department={child}
                toggleUser={toggleUser}
                checkedUsers={checkedUsers}
                setCheckedUsers={setCheckedUsers}
                search={search}
                onDelete={onDelete}
                onGetQr={onGetQr}
                eventId={eventId}
                refetchSegments={refetchSegments}
                refetchCategories={refetchCategories}
                eventDateEnd={eventDateEnd}
                eventDateStart={eventDateStart}
              />
            ))}
          </div>
        </div>
      )}
    </>
  );
};

export const SegmentStructureTree = React.memo(SegmentStructureTreeComponent);
